import { useQueryClient } from '@tanstack/react-query'
import { type Coupon } from '@/features/shop/services/Coupons/types'
import { useCurrentActiveOrderId } from '@/features/shop/state/SelectedActiveOrder/hooks'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import {
  couponsSavingsQueryOptions,
  useQueryCouponsSavings,
} from '@/features/shop/services/Coupons/queries'
import { formatPrice } from '@/features/shared/utils/formatter'
import { useShowToast } from '@/features/shared/state/Toast/hooks'
import { ToastType } from '@/features/shared/constants/toastTypes'
import { refreshCoupons, resetCouponUsageData } from '@/features/shop/services/Coupons/utils'
import {
  getCouponPromoCardProperties,
  type CouponPromoCardProps,
} from '@/features/shared/analytics/promoCard'
import { useMutationClipCoupon } from '@/features/shop/services/Coupons/mutations'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { useUserId } from '@/features/account/services/User/hooks'

export type CouponClippedState = ReturnType<typeof useCouponSavings>
export const useCouponSavings = (
  coupon:
    | {
        couponId: string
        clippedAt: string
      }
    | undefined,
) => {
  coupon = coupon ?? { couponId: '', clippedAt: '' }
  const couponSavings = useQueryCouponsSavings().data?.[coupon.couponId]
  const {
    clippedAt,
    isFulfilled,
    appliedProductQty = 0,
    completionPercent = 0,
    qualifiedProductIdsInOrder = [],
  } = couponSavings ?? {}
  return {
    isClipped: Boolean(coupon.clippedAt || clippedAt),
    isFulfilled: Boolean(isFulfilled),
    completionPercent,
    qualifiedProductIdsInOrder,
    quantityNeeded: appliedProductQty - qualifiedProductIdsInOrder.length,
  }
}

export const useRefreshCouponsAndShowCouponToast = () => {
  const { showToast } = useShowToast()
  const getCouponsSavings = useGetCouponsSavings()

  return async (couponId: string): Promise<boolean> => {
    const findCoupon = () => getCouponsSavings()?.[couponId]

    const previousCoupon = findCoupon()
    await refreshCoupons()
    const currentCoupon = findCoupon()
    /**
     * The coupon_service/v1/coupons.calculateSavings service does not return a coupon back in the API response
     * if it is no longer applicable to the cart/order. This means we need to manually set the couponForToast
     * variable to the previous coupon in that scenario.
     */
    let couponForToast = currentCoupon || previousCoupon
    if (couponForToast?.clippedAt) {
      // when the current coupon is no longer applicable to the cart/order, reset the coupon values
      if (!currentCoupon && previousCoupon) {
        couponForToast = resetCouponUsageData(previousCoupon)
      }
      const {
        faceValue = 0,
        isFulfilled = false,
        appliedProductQty = 0,
        completionPercent = 0,
        qualifiedProductIdsInOrder = [],
      } = couponForToast
      const quantityNeeded = appliedProductQty - qualifiedProductIdsInOrder.length
      // If the coupon is already fulfilled, don't show the toast
      if (Boolean(previousCoupon?.isFulfilled) && isFulfilled) return false
      const formattedValue = formatPrice(faceValue)

      showToast(ToastType.COUPON_PROGRESS_TOAST, {
        optionalStyling: {
          borderRadius: 8,
          boxShadow: 'none',
        },
        ariaLabel: isFulfilled
          ? `${formattedValue} coupon applied at checkout`
          : `Add ${quantityNeeded} more to get ${formattedValue} off`,
        props: {
          prevProgress: (previousCoupon?.completionPercent ?? 0) / 100,
          progress: completionPercent,
          quantityNeeded,
          formattedValue,
          isFulfilled,
          showProgressBar: appliedProductQty > 3,
        },
      })
      return true
    }
    return false
  }
}

export const useClipCouponAndCalculateSavings = () => {
  const { mutateAsync: clipCoupon, error, isError, isPending } = useMutationClipCoupon()
  const { trackCMSPromoCardClicked } = useCMSTrackingEvents()

  const clipCouponAndCalculateSavings = async (
    coupon: { couponId: string; summary?: string },
    eventProperties?: Pick<CouponPromoCardProps, 'listIndex' | 'subLocation'>,
  ) => {
    await clipCoupon(coupon.couponId)
    refreshCoupons()
    trackCMSPromoCardClicked(
      getCouponPromoCardProperties({
        ...eventProperties,
        coupon,
        actionType: 'clip',
        couponClipped: true,
      }),
    )
  }

  return {
    error,
    isError,
    isPending,
    clipCouponAndCalculateSavings,
  }
}

const useGetCouponsSavings = () => {
  const queryClient = useQueryClient()
  const storeParams = useStoreParams()
  const currentActiveOrderId = useCurrentActiveOrderId()
  const userId = useUserId() || 0
  return () =>
    queryClient.getQueryData(
      couponsSavingsQueryOptions({
        currentActiveOrderId,
        storeParams,
        userId,
      }).queryKey,
    )
}

export const useGetAppliedCoupons = () => {
  const couponsSavings = useQueryCouponsSavings().data ?? {}
  return () =>
    Object.values(couponsSavings).reduce<Coupon[]>((acc, coupon) => {
      return coupon.isFulfilled ? [...acc, coupon] : acc
    }, [])
}
